import * as R from 'ramda';
import {useMemo} from 'react';

import {useRoutes} from 'iss/hooks/useRoutes';

/** A hook that provides a state store backed by
 * the current URL location of react-router. The data is stored
 * in the query part of the URL so
 * /app/route?data=1&str=fish
 * becomes a variable with the value
 * {str: "fish", data: "1"}
 * All the data is stored in string form so some conversion
 * logic needs to be applied to non string values
 *
 * Params:
 *
 * config :: object, optional
 * A config object with the following optional keys:
 *
 *   defaults :: object
 *   Default value for the state
 *
 *   parse :: object
 *   An object representing possible parsers for the query parameters.
 *   For example to parse a boolean flag as a boolean you can use {flag: val => val == "true"}
 *
 *  Returns:
 *  [queryParams, setQueryParams]
 *  An array with the parsed query params and a setter for the parameters
 *
 **/
export function useQueryState(config) {
  const {history} = useRoutes();
  const query = history.location.search;
  //Memoize so that the query state objects share identity when the query doesn't change
  const state = useMemo(() => R.pipe(
    queryToObject,
    R.evolve(R.propOr({}, "parse", config)),
    R.mergeRight(R.propOr({}, "defaults", config)),
  )(query), [query]);
  const setState = (state) => {
    const newURL = history.location.pathname + "?" + objectToQuery(state);
    history.replace(newURL);
  };

  return [state, setState];
}

function queryToObject(query) {
  const searchParams = new URLSearchParams(query);
  const obj = {};
  for(let [key, val] of searchParams.entries()) {
    obj[key] = val;
  }
  return obj;
}

function objectToQuery(obj) {
  const params = new URLSearchParams();
  R.forEachObjIndexed((value, key) => params.set(key, value), obj);
  return params.toString();
}




// WEBPACK FOOTER //
// ./modules/issFrontend/hooks/useQueryState.jsx